import React, { useState } from 'react';
import { XIcon } from '@phosphor-icons/react';
import * as Sentry from '@sentry/react';
import useCustomers, {
  CustomerPreviewDTO,
} from 'src/api/accounts-receivable/customers/useCustomers';
import useCustomersByIds, {
  CustomerBasicInfo,
} from 'src/api/accounts-receivable/customers/useCustomersByIds';

import {
  SalesOrderDraftStatus,
  SalesOrderSource,
} from 'src/types/application/SalesOrderDraftDTO';
import { WatchStatus } from '@/types/application/WatcherDTO';
import { Autocomplete } from '@/components/common/inputs/autocomplete';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { FloatingLabelWrapper } from '@/components/common/inputs/floating-label-wrapper';

export interface FilterValues {
  customerIds?: string[];
  excludedCustomerIds?: string[];
  statuses?: SalesOrderDraftStatus[];
  sources?: SalesOrderSource[];
  watchStatus?: WatchStatus[];
  purchaseOrderNumber?: string;
}

interface Props {
  filters: FilterValues;
  onFiltersChange: (filters: FilterValues) => void;
}

const statusOptions = [
  { value: SalesOrderDraftStatus.RECEIVED, label: 'New' },
  // { value: SalesOrderDraftStatus.PENDING_REVIEW, label: 'Pending Review' },
  { value: SalesOrderDraftStatus.APPROVED, label: 'Approved' },
  { value: SalesOrderDraftStatus.REJECTED, label: 'Rejected' },
];

// Exhaustive source options - will cause compilation error if new source added
const getSourceLabel = (source: SalesOrderSource): string => {
  switch (source) {
    case SalesOrderSource.Edi:
      return 'EDI';
    case SalesOrderSource.Email:
      return 'Email';
    case SalesOrderSource.DirectEntry:
      return 'Direct Entry';
    case SalesOrderSource.FileUpload:
      return 'File Upload';
    default: {
      // Exhaustiveness check - will cause TS error if a new source is added
      const exhaustiveCheck: never = source;
      const error = new Error(`Unhandled SalesOrderSource: ${exhaustiveCheck}`);
      console.error(error);
      Sentry.captureException(error);
      return 'Unknown'; // Fallback label
    }
  }
};

const sourceOptions = Object.values(SalesOrderSource).map((source) => ({
  value: source,
  label: getSourceLabel(source),
}));

const watchStatusOptions = [
  { value: 'watching' as WatchStatus, label: 'Watching' },
  { value: 'assigned' as WatchStatus, label: 'Assigned' },
];

// Helper function to convert CustomerPreviewDTO to CustomerBasicInfo
const convertToBasicInfo = (
  customer: CustomerPreviewDTO,
): CustomerBasicInfo => ({
  customerId: customer.id,
  name: customer.name,
});

const SalesOrderFilters: React.FC<Props> = ({ filters, onFiltersChange }) => {
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [excludedCustomerSearchQuery, setExcludedCustomerSearchQuery] =
    useState('');

  // Fetch customers for autocomplete
  const { data: customersData, isLoading: customersLoading } = useCustomers({
    pageSize: 10,
    name: customerSearchQuery.length > 2 ? customerSearchQuery : undefined,
  });

  // Fetch customers for excluded autocomplete
  const { data: excludedCustomersData, isLoading: excludedCustomersLoading } =
    useCustomers({
      pageSize: 10,
      name:
        excludedCustomerSearchQuery.length > 2
          ? excludedCustomerSearchQuery
          : undefined,
    });

  // Fetch selected customers by IDs to show their names
  const { data: selectedCustomerDetails } = useCustomersByIds(
    filters.customerIds,
    !!filters.customerIds?.length,
  );

  // Fetch excluded customers by IDs to show their names
  const { data: excludedCustomerDetails } = useCustomersByIds(
    filters.excludedCustomerIds,
    !!filters.excludedCustomerIds?.length,
  );

  // Derive selected customers from fetched details (no useEffect needed)
  const selectedCustomers = React.useMemo(
    () => selectedCustomerDetails || [],
    [selectedCustomerDetails],
  );
  const excludedCustomers = React.useMemo(
    () => excludedCustomerDetails || [],
    [excludedCustomerDetails],
  );

  // Detect orphaned customer IDs (exist in filters but not found in the current environment)
  // This can happen when switching between environments locally
  const orphanedCustomerIds = React.useMemo(() => {
    if (!filters.customerIds?.length) return [];
    const foundIds = new Set(selectedCustomers.map((c) => c.customerId));
    return filters.customerIds.filter((id) => !foundIds.has(id));
  }, [filters.customerIds, selectedCustomers]);

  const orphanedExcludedCustomerIds = React.useMemo(() => {
    if (!filters.excludedCustomerIds?.length) return [];
    const foundIds = new Set(excludedCustomers.map((c) => c.customerId));
    return filters.excludedCustomerIds.filter((id) => !foundIds.has(id));
  }, [filters.excludedCustomerIds, excludedCustomers]);

  // Merge selected customers with search results to ensure selected items are always available
  const customerOptionsWithSelected = React.useMemo(() => {
    const searchResults = (customersData?.edges || []).map(convertToBasicInfo);
    const selectedMap = new Map(
      selectedCustomers.map((c) => [c.customerId, c]),
    );

    // Add selected customers that aren't in search results
    const merged = [...searchResults];
    selectedCustomers.forEach((selected) => {
      if (!searchResults.find((r) => r.customerId === selected.customerId)) {
        merged.push(selected);
      }
    });

    return merged;
  }, [customersData?.edges, selectedCustomers]);

  // Merge excluded customers with search results
  const excludedCustomerOptionsWithSelected = React.useMemo(() => {
    const searchResults = (excludedCustomersData?.edges || []).map(
      convertToBasicInfo,
    );

    // Add excluded customers that aren't in search results
    const merged = [...searchResults];
    excludedCustomers.forEach((excluded) => {
      if (!searchResults.find((r) => r.customerId === excluded.customerId)) {
        merged.push(excluded);
      }
    });

    return merged;
  }, [excludedCustomersData?.edges, excludedCustomers]);

  const handleCustomerChange = (customerIds: string[]) => {
    onFiltersChange({
      ...filters,
      customerIds: customerIds.length ? customerIds : undefined,
    });
  };

  const handleExcludedCustomerChange = (customerIds: string[]) => {
    onFiltersChange({
      ...filters,
      excludedCustomerIds: customerIds.length ? customerIds : undefined,
    });
  };

  const handleStatusChange = (statuses: string[]) => {
    onFiltersChange({
      ...filters,
      statuses: statuses.length
        ? (statuses as SalesOrderDraftStatus[])
        : undefined,
    });
  };

  const handleSourceChange = (sources: string[]) => {
    onFiltersChange({
      ...filters,
      sources: sources.length ? (sources as SalesOrderSource[]) : undefined,
    });
  };

  const handleWatchStatusChange = (watchStatuses: string[]) => {
    onFiltersChange({
      ...filters,
      watchStatus: watchStatuses.length
        ? (watchStatuses as WatchStatus[])
        : undefined,
    });
  };

  const handlePONumberChange = (value: string) => {
    onFiltersChange({
      ...filters,
      purchaseOrderNumber: value || undefined,
    });
  };

  const hasActiveFilters =
    (filters.customerIds && filters.customerIds.length) ||
    (filters.excludedCustomerIds && filters.excludedCustomerIds.length) ||
    (filters.statuses && filters.statuses.length) ||
    (filters.sources && filters.sources.length) ||
    (filters.watchStatus && filters.watchStatus.length) ||
    !!filters.purchaseOrderNumber;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {/* Filter by Customer */}
        <FloatingLabelWrapper
          label="Filter by Customer"
          hasValue={!!(filters.customerIds && filters.customerIds.length > 0)}
        >
          <Autocomplete
            multiSelect
            options={customerOptionsWithSelected}
            searchValue={customerSearchQuery}
            onSearchValueChange={setCustomerSearchQuery}
            getValue={(option) => option.customerId}
            getLabel={(option) => option.name}
            value={filters.customerIds}
            onValueChange={handleCustomerChange}
            placeholder=""
            searchPlaceholder="Start typing to search customers..."
            emptyMessage={
              customersLoading ? 'Loading...' : 'No customers found'
            }
            className="w-full"
          />
        </FloatingLabelWrapper>

        {/* Exclude Customers */}
        <FloatingLabelWrapper
          label="Exclude Customers"
          hasValue={
            !!(
              filters.excludedCustomerIds &&
              filters.excludedCustomerIds.length > 0
            )
          }
        >
          <Autocomplete
            multiSelect
            options={excludedCustomerOptionsWithSelected}
            searchValue={excludedCustomerSearchQuery}
            onSearchValueChange={setExcludedCustomerSearchQuery}
            getValue={(option) => option.customerId}
            getLabel={(option) => option.name}
            value={filters.excludedCustomerIds}
            onValueChange={handleExcludedCustomerChange}
            placeholder=""
            searchPlaceholder="Start typing to search customers..."
            emptyMessage={
              excludedCustomersLoading ? 'Loading...' : 'No customers found'
            }
            className="w-full"
          />
        </FloatingLabelWrapper>

        {/* Filter by PO Number */}
        <FloatingLabelWrapper
          label="Filter by PO Number"
          hasValue={!!filters.purchaseOrderNumber}
        >
          <Input
            placeholder=""
            value={filters.purchaseOrderNumber || ''}
            onChange={(e) => handlePONumberChange(e.target.value)}
          />
        </FloatingLabelWrapper>

        {/* Filter by Status */}
        <FloatingLabelWrapper
          label="Filter by Status"
          hasValue={!!(filters.statuses && filters.statuses.length > 0)}
        >
          <Autocomplete
            multiSelect
            options={statusOptions}
            searchValue=""
            onSearchValueChange={() => {}}
            getValue={(option) => option.value}
            getLabel={(option) => option.label}
            value={filters.statuses}
            onValueChange={handleStatusChange}
            placeholder=""
            searchPlaceholder="Search status..."
            className="w-full"
          />
        </FloatingLabelWrapper>

        {/* Filter by Source */}
        <FloatingLabelWrapper
          label="Filter by Source"
          hasValue={!!(filters.sources && filters.sources.length > 0)}
        >
          <Autocomplete
            multiSelect
            options={sourceOptions}
            searchValue=""
            onSearchValueChange={() => {}}
            getValue={(option) => option.value}
            getLabel={(option) => option.label}
            value={filters.sources}
            onValueChange={handleSourceChange}
            placeholder=""
            searchPlaceholder="Search source..."
            className="w-full"
          />
        </FloatingLabelWrapper>

        {/* Watching / Assigned */}
        <FloatingLabelWrapper
          label="Watching / Assigned"
          hasValue={!!(filters.watchStatus && filters.watchStatus.length > 0)}
        >
          <Autocomplete
            multiSelect
            options={watchStatusOptions}
            searchValue=""
            onSearchValueChange={() => {}}
            getValue={(option) => option.value}
            getLabel={(option) => option.label}
            value={filters.watchStatus}
            onValueChange={handleWatchStatusChange}
            placeholder=""
            searchPlaceholder="Search..."
            className="w-full"
          />
        </FloatingLabelWrapper>
      </div>

      {/* Active filters display */}
      {hasActiveFilters && (
        <div className="flex justify-between items-center">
          <div className="flex gap-2 flex-wrap">
            {selectedCustomers.map((cust) => (
              <Badge
                key={cust.customerId}
                variant="outline"
                className="cursor-pointer hover:bg-secondary pr-1"
              >
                Customer: {cust.name}
                <button
                  onClick={() => {
                    const remaining =
                      filters.customerIds?.filter(
                        (id) => id !== cust.customerId,
                      ) || [];
                    handleCustomerChange(remaining);
                  }}
                  className="ml-1 rounded-full hover:bg-secondary/80 p-0.5"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {/* Orphaned customer IDs - exist in filters but not found in current environment */}
            {orphanedCustomerIds.map((id) => (
              <Badge
                key={id}
                variant="outline"
                className="cursor-pointer hover:bg-secondary pr-1 border-dashed border-amber-400"
                title={`Customer ID: ${id} (not found in current environment)`}
              >
                Customer: Unknown
                <button
                  onClick={() => {
                    const remaining =
                      filters.customerIds?.filter((cid) => cid !== id) || [];
                    handleCustomerChange(remaining);
                  }}
                  className="ml-1 rounded-full hover:bg-secondary/80 p-0.5"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {excludedCustomers.map((cust) => (
              <Badge
                key={cust.customerId}
                variant="exclude"
                className="cursor-pointer hover:bg-red-100 pr-1"
              >
                Exclude: {cust.name}
                <button
                  onClick={() => {
                    const remaining =
                      filters.excludedCustomerIds?.filter(
                        (id) => id !== cust.customerId,
                      ) || [];
                    handleExcludedCustomerChange(remaining);
                  }}
                  className="ml-1 rounded-full hover:bg-red-200 p-0.5"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {/* Orphaned excluded customer IDs - exist in filters but not found in current environment */}
            {orphanedExcludedCustomerIds.map((id) => (
              <Badge
                key={id}
                variant="exclude"
                className="cursor-pointer hover:bg-red-100 pr-1 border-dashed border-amber-400"
                title={`Customer ID: ${id} (not found in current environment)`}
              >
                Exclude: Unknown
                <button
                  onClick={() => {
                    const remaining =
                      filters.excludedCustomerIds?.filter(
                        (cid) => cid !== id,
                      ) || [];
                    handleExcludedCustomerChange(remaining);
                  }}
                  className="ml-1 rounded-full hover:bg-red-200 p-0.5"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {(filters.statuses || []).map((status) => (
              <Badge
                key={status}
                variant="outline"
                className="cursor-pointer hover:bg-secondary pr-1"
              >
                Status: {statusOptions.find((s) => s.value === status)?.label}
                <button
                  onClick={() => {
                    const newStatuses = (filters.statuses || []).filter(
                      (s) => s !== status,
                    );
                    handleStatusChange(newStatuses);
                  }}
                  className="ml-1 rounded-full hover:bg-secondary/80 p-0.5"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {(filters.sources || []).map((src) => (
              <Badge
                key={src}
                variant="outline"
                className="cursor-pointer hover:bg-secondary pr-1"
              >
                Source: {sourceOptions.find((s) => s.value === src)?.label}
                <button
                  onClick={() => {
                    const newSources = (filters.sources || []).filter(
                      (s) => s !== src,
                    );
                    handleSourceChange(newSources);
                  }}
                  className="ml-1 rounded-full hover:bg-secondary/80 p-0.5"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {(filters.watchStatus || []).map((watchStatus) => (
              <Badge
                key={watchStatus}
                variant="outline"
                className="cursor-pointer hover:bg-secondary pr-1"
              >
                Watch:{' '}
                {watchStatusOptions.find((w) => w.value === watchStatus)?.label}
                <button
                  onClick={() => {
                    const newWatchStatuses = (filters.watchStatus || []).filter(
                      (w) => w !== watchStatus,
                    );
                    handleWatchStatusChange(newWatchStatuses);
                  }}
                  className="ml-1 rounded-full hover:bg-secondary/80 p-0.5"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {filters.purchaseOrderNumber && (
              <Badge
                variant="outline"
                className="cursor-pointer hover:bg-secondary pr-1"
              >
                PO: {filters.purchaseOrderNumber}
                <button
                  onClick={() => handlePONumberChange('')}
                  className="ml-1 rounded-full hover:bg-secondary/80 p-0.5"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesOrderFilters;
